import { uniq } from "lodash-es";

import type {
  DictsysGermanNounInflectionScheme,
  DictsysInflectionSchemeCell,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeGermanNounGenders,
  DictsysInflectionSchemeTable
} from "@/types";

function getHeader(genders: DictsysInflectionSchemeGermanNounGenders): string {
  const headerMap: Record<string, string> = {
    masculine: "Maskulinum",
    feminine: "Femininum",
    neutral: "Neutrum",
    void: "Findes kun i flertal"
  };

  return headerMap[Object.keys(genders)[0]];
}

function getBodyCell(
  definiteContent?: DictsysInflectionSchemeContent[],
  indefiniteContent?: DictsysInflectionSchemeContent[]
): DictsysInflectionSchemeCell {
  const cell: DictsysInflectionSchemeCell = { type: "body", elements: [] };

  if (!definiteContent || !indefiniteContent) {
    return cell;
  }

  cell.elements.push({
    auxiliaryVerbs: [definiteContent[0].prefix, indefiniteContent[0].prefix],
    texts: [
      uniq([
        ...definiteContent.map((definite) => definite.inflexion),
        ...indefiniteContent.map((indefinite) => indefinite.inflexion)
      ]).join(" / ")
    ]
  });

  return cell;
}

export function getTable(inflectionScheme: DictsysGermanNounInflectionScheme): DictsysInflectionSchemeTable | null {
  if (!inflectionScheme.noun) {
    return null;
  }

  const noun = inflectionScheme.noun;
  const article = noun.masculine || noun.feminine || noun.neutral || noun.void;

  if (!article) {
    return null;
  }

  const subHeader: DictsysInflectionSchemeCell[] = [{ type: "sub-header", elements: [] }];
  const nominative: DictsysInflectionSchemeCell[] = [{ type: "sub-header", elements: [{ texts: ["Nominativ"] }] }];
  const accusative: DictsysInflectionSchemeCell[] = [{ type: "sub-header", elements: [{ texts: ["Akkusativ"] }] }];
  const genitive: DictsysInflectionSchemeCell[] = [{ type: "sub-header", elements: [{ texts: ["Genitiv"] }] }];
  const dative: DictsysInflectionSchemeCell[] = [{ type: "sub-header", elements: [{ texts: ["Dativ"] }] }];

  // Simply checking for singular, through one of the article types and cases
  if (article.definite.nominative.singular) {
    subHeader.push({ type: "sub-header", elements: [{ texts: ["Singularis"], comment: "(ental)" }] });
    nominative.push(getBodyCell(article.definite.nominative.singular, article.indefinite.nominative.singular));
    accusative.push(getBodyCell(article.definite.akkusative.singular, article.indefinite.akkusative.singular));
    genitive.push(getBodyCell(article.definite.genitive.singular, article.indefinite.genitive.singular));
    dative.push(getBodyCell(article.definite.dative.singular, article.indefinite.dative.singular));
  }

  // Simply checking for plural, through one of the article types and cases
  if (article.definite.nominative.plural) {
    subHeader.push({ type: "sub-header", elements: [{ texts: ["Pluralis"], comment: "(flertal)" }] });
    nominative.push(getBodyCell(article.definite.nominative.plural, article.indefinite.nominative.plural));
    accusative.push(getBodyCell(article.definite.akkusative.plural, article.indefinite.akkusative.plural));
    genitive.push(getBodyCell(article.definite.genitive.plural, article.indefinite.genitive.plural));
    dative.push(getBodyCell(article.definite.dative.plural, article.indefinite.dative.plural));
  }

  return {
    header: getHeader(noun),
    cells: [subHeader, nominative, accusative, genitive, dative]
  };
}
